import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import StaffProductivity from './StaffProductivity';
import { fetchDashboardData, AUTO_REFRESH_SECONDS } from '../../operations/jiraService';
import { DashboardData, Issue, Reporter } from './types';

// ======================================================================
//  STATUS CONFIG -- matches actual Jira instance statuses
// ======================================================================

interface StatusConfig {
  key: string;
  stage: number;
  color: string;
  bg: string;
  label: string;
}

const STATUSES: StatusConfig[] = [
  { key: "To Do",                     stage: 0, color: "#8B8BFF", bg: "#8B8BFF1A", label: "To Do" },
  { key: "In Progress",               stage: 1, color: "#00b8ff", bg: "#00b8ff1A", label: "In Progress" },
  { key: "Feature Branch",            stage: 1, color: "#33C9FF", bg: "#33C9FF1A", label: "Feature Branch" },
  { key: "DEPLOYMENT IN DEV",         stage: 1, color: "#0099DD", bg: "#0099DD1A", label: "Dev Deploy" },
  { key: "Unit Testing",              stage: 2, color: "#FBBF24", bg: "#FBBF241A", label: "Unit Testing" },
  { key: "Dev Deployment",            stage: 2, color: "#F59E0B", bg: "#F59E0B1A", label: "Dev Deployment" },
  { key: "Dev QA",                    stage: 2, color: "#FB923C", bg: "#FB923C1A", label: "Dev QA" },
  { key: "CODE REVIEW",               stage: 2, color: "#F97316", bg: "#F973161A", label: "Code Review" },
  { key: "UAT Deployment",            stage: 3, color: "#8B33FF", bg: "#8B33FF1A", label: "UAT Deploy" },
  { key: "UAT QA",                    stage: 3, color: "#9B4DFF", bg: "#9B4DFF1A", label: "UAT QA" },
  { key: "DEPLOYMENT IN UAT",         stage: 3, color: "#6c00f6", bg: "#6c00f61A", label: "In UAT" },
  { key: "Ready for Prod Deployment", stage: 3, color: "#B366FF", bg: "#B366FF1A", label: "Ready Prod" },
  { key: "Ready for Production",      stage: 3, color: "#A04DFF", bg: "#A04DFF1A", label: "Ready Prod" },
  { key: "DEPLOYMENT IN PRODUCTON",   stage: 4, color: "#a1f919", bg: "#a1f9191A", label: "Production" },
  { key: "Done",                      stage: 4, color: "#7BC410", bg: "#7BC4101A", label: "Done" },
];

const STATUS_MAP: Record<string, StatusConfig> = Object.fromEntries(STATUSES.map(s => [s.key, s]));
const STAGES: string[] = ["To Do", "Development", "Testing/QA", "UAT", "Done"];
const STAGE_COLORS: string[] = ["#8B8BFF", "#00b8ff", "#FBBF24", "#6c00f6", "#a1f919"];

// Staff members excluded from all dashboard views
const EXCLUDED_STAFF: string[] = [
  "faiyaz ali syed", "chandani doshi", "kirill", "vipul", "suren", "harmeet", "poonam", "pritam",
];
function isExcludedStaff(name: string): boolean {
  const n = (name || "").toLowerCase();
  return EXCLUDED_STAFF.some(ex => n.includes(ex));
}

function stageIndex(statusGroup: string): number {
  const map: Record<string, number> = { todo: 0, dev: 1, testing: 2, uat: 3, readyProd: 3, production: 4, done: 4 };
  return map[statusGroup] ?? 0;
}

function isDone(issue: Issue): boolean {
  return issue.statusGroup === "done" || issue.statusGroup === "production";
}

// Solved = fix deployed to at least Dev environment or beyond
const BUG_SOLVED_STATUSES: string[] = [
  "Dev Deployment", "DEPLOYMENT IN DEV", "DEPLOYMENT IN SPRINT BRANCH",
  "Dev QA", "CODE REVIEW", "Unit Testing",
  "UAT QA", "UAT Deployment", "DEPLOYMENT IN UAT",
  "Ready for Prod Deployment", "Ready for Production",
  "DEPLOYMENT IN PRODUCTON", "Done",
];
function isBugSolved(issue: Issue): boolean {
  return BUG_SOLVED_STATUSES.includes(issue.status);
}

// ======================================================================
//  UTILITY HELPERS
// ======================================================================

function initials(name: string): string {
  const p = (name || "?").split(" ");
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : p[0][0].toUpperCase();
}

function statusColor(status: string): { color: string; bg: string } {
  return STATUS_MAP[status] || { color: "#71717A", bg: "#71717A1A" };
}

function statusLabel(status: string): string {
  return STATUS_MAP[status]?.label || status;
}

function plainEnvLabel(statusGroup: string): string {
  const map: Record<string, string> = { todo: "Not Started", dev: "In Dev", testing: "In QA", uat: "In UAT", readyProd: "Ready for Prod", production: "In Prod", done: "Done" };
  return map[statusGroup] || statusGroup || "Unknown";
}

function envLabelColor(statusGroup: string): string {
  const map: Record<string, string> = { todo: "#8B8BFF", dev: "#00b8ff", testing: "#FBBF24", uat: "#6c00f6", readyProd: "#B366FF", production: "#a1f919", done: "#7BC410" };
  return map[statusGroup] || "#71717A";
}

// ======================================================================
//  SVG CHART COMPONENTS
// ======================================================================

interface PipelineChartProps {
  counts: number[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function PipelineChart({ counts }: PipelineChartProps) {
  const W = 560, H = 100;
  const total = counts.reduce((a, b) => a + b, 0) || 1;
  let x = 0;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {STAGES.map((stage, i) => {
        const w = Math.max((counts[i] / total) * (W - 24), 4);
        const cx = x + 10;
        x += w + 4;
        return (
          <g key={stage}>
            <rect x={cx} y={20} width={w} height={40} rx={6} fill={STAGE_COLORS[i]} opacity={0.2} />
            <rect x={cx} y={20} width={Math.max(w * 0.7, 4)} height={40} rx={6} fill={STAGE_COLORS[i]} opacity={0.6} />
            <text x={cx + w / 2} y={45} textAnchor="middle" fontSize={14} fontWeight={700} fill={STAGE_COLORS[i]} fontFamily="JetBrains Mono">{counts[i]}</text>
            <text x={cx + w / 2} y={78} textAnchor="middle" fontSize={9} fill="#6B7280" fontFamily="DM Sans" fontWeight={500}>{stage}</text>
          </g>
        );
      })}
    </svg>
  );
}

interface MiniDonutProps {
  pct: number;
  color?: string;
  size?: number;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function MiniDonut({ pct, color = "#6c00f6", size = 48 }: MiniDonutProps) {
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={4} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={4}
        strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x={size / 2} y={size / 2 + 4} textAnchor="middle" fontSize={11} fontWeight={700} fill="#111827" fontFamily="JetBrains Mono">
        {pct}%
      </text>
    </svg>
  );
}

// ======================================================================
//  UI SUB-COMPONENTS
// ======================================================================

interface AvatarProps {
  name: string;
}

function Avatar({ name }: AvatarProps) {
  return (
    <div className="w-8 h-8 text-xs rounded-full bg-gradient-to-br from-zen-purple to-zen-cyan flex items-center justify-center font-bold text-white shrink-0">
      {initials(name)}
    </div>
  );
}

interface BadgeProps {
  status: string;
}

function Badge({ status }: BadgeProps) {
  const sc = statusColor(status);
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{ color: sc.color, background: sc.bg }}>
      {statusLabel(status)}
    </span>
  );
}

interface TypeIconProps {
  type: string;
}

function TypeIcon({ type }: TypeIconProps) {
  const colors: Record<string, string> = { Bug: "#EF4444", Task: "#3B82F6", Story: "#22D3EE", Epic: "#6c00f6", "Sub-task": "#71717A" };
  const c = colors[type] || "#71717A";
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[9px] font-bold shrink-0"
      style={{ background: c + "22", color: c }}>
      {type?.[0] || "T"}
    </span>
  );
}

interface KPICardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon?: React.ReactNode;
}

function KPICard({ label, value, sub, icon }: KPICardProps) {
  return (
    <div className="bg-surface-2 border border-edge-1 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium text-txt-3 uppercase tracking-wider">{label}</span>
        {icon && <span className="text-txt-4 text-sm">{icon}</span>}
      </div>
      <div className="text-2xl font-bold text-txt-1 font-mono">{value}</div>
      {sub && <span className="text-[11px] text-txt-4">{sub}</span>}
    </div>
  );
}

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}

function SectionHeader({ title, subtitle, right }: SectionHeaderProps) {
  return (
    <div className="flex items-end justify-between mb-3">
      <div>
        <h3 className="text-base font-bold text-txt-1">{title}</h3>
        {subtitle && <p className="text-xs text-txt-3 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

// ======================================================================
//  INTERNAL TYPES
// ======================================================================

interface StaffMember {
  name: string;
  issues: Issue[];
  backlog: number;
  totalSP: number;
}

interface StakeholderMember {
  name: string;
  issues: Issue[];
  total: number;
  bugs: number;
  tasks: number;
  epics: number;
  done: number;
  stageCounts: number[];
}

interface InsightItem {
  type: string;
  text: string;
}

interface Insights {
  health: InsightItem[];
  performance: InsightItem[];
  risks: InsightItem[];
}

interface SimpleBucket {
  label: string;
  count: number;
  color: string;
  pct: number;
}

interface BugSummary {
  raised: number;
  solved: number;
  hotfixes: number;
  resolvePct: number;
  resolveColor: string;
  sentence: string;
}

interface DeploymentGroup {
  type: string;
  issues: Issue[];
}

interface DeploymentBucket {
  label: string;
  items: Issue[];
  grouped: DeploymentGroup[];
}

interface DeploymentLists {
  dev: DeploymentBucket;
  uat: DeploymentBucket;
  prod: DeploymentBucket;
}

interface SprintSignal {
  label: string;
  color: string;
  bg: string;
}

interface TabConfig {
  key: string;
  label: string;
}

// ======================================================================
//  MAIN DASHBOARD
// ======================================================================

export default function DarkDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [countdown, setCountdown] = useState<number>(AUTO_REFRESH_SECONDS);
  const [projectFilter, setProjectFilter] = useState<string>("ALL");
  const [search, setSearch] = useState<string>("");
  const [tab, setTab] = useState<string>("overview");
  const [expandedStaff, setExpandedStaff] = useState<Record<string, boolean>>({});
  const [expandedStakeholder, setExpandedStakeholder] = useState<Record<string, boolean>>({});
  const [expandedDeployments, setExpandedDeployments] = useState<Record<string, boolean>>({});

  // --- Fetch data ---
  const refresh = useCallback(async () => {
    try {
      setErr(null);
      const d = await fetchDashboardData();
      setData(d);
    } catch (error: unknown) {
      setErr(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
      setCountdown(AUTO_REFRESH_SECONDS);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const t = setInterval(() => {
      setCountdown(p => {
        if (p <= 1) { refresh(); return AUTO_REFRESH_SECONDS; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [refresh]);

  // --- Filtered issues ---
  const filteredIssues = useMemo((): Issue[] => {
    if (!data?.issues) return [];
    let issues = data.issues;
    if (projectFilter !== "ALL") issues = issues.filter((i: Issue) => i.project === projectFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      issues = issues.filter((i: Issue) =>
        i.key.toLowerCase().includes(q) ||
        i.summary.toLowerCase().includes(q) ||
        i.assignee.toLowerCase().includes(q)
      );
    }
    return issues;
  }, [data?.issues, projectFilter, search]);

  const filteredBacklog = useMemo((): Issue[] => {
    if (!data?.backlogIssues) return [];
    if (projectFilter === "ALL") return data.backlogIssues;
    return data.backlogIssues.filter((i: Issue) => i.project === projectFilter);
  }, [data?.backlogIssues, projectFilter]);

  // --- Status counts ---
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const statusCounts = useMemo((): Record<string, number> => {
    const c: Record<string, number> = {};
    STATUSES.forEach(s => { c[s.key] = 0; });
    filteredIssues.forEach((i: Issue) => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [filteredIssues]);

  // --- Stage counts ---
  const stageCounts = useMemo((): number[] => {
    const c = [0, 0, 0, 0, 0];
    filteredIssues.forEach((i: Issue) => { c[stageIndex(i.statusGroup)]++; });
    return c;
  }, [filteredIssues]);

  // --- Type metrics (client-side recompute for project filter) ---
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const typeMetrics = useMemo(() => {
    const m = { bugs: { total: 0, solved: 0 }, tasks: { total: 0, solved: 0 }, epics: { total: 0, closed: 0 } };
    filteredIssues.forEach((i: Issue) => {
      const t = (i.type || "").toLowerCase();
      if (t === "bug") { m.bugs.total++; if (isBugSolved(i)) m.bugs.solved++; }
      else if (t === "epic") { m.epics.total++; if (isDone(i)) m.epics.closed++; }
      else { m.tasks.total++; if (isDone(i)) m.tasks.solved++; }
    });
    return m;
  }, [filteredIssues]);

  // --- Bug metrics ---
  const bugMetrics = useMemo(() => {
    const bugs = filteredIssues.filter((i: Issue) => (i.type || "").toLowerCase() === "bug");
    return {
      raised: bugs.length,
      solved: bugs.filter(isBugSolved).length,
      hotfixes: bugs.filter((i: Issue) => (i.priority || "").toLowerCase() === "highest" || (i.summary || "").toLowerCase().includes("hotfix")).length,
    };
  }, [filteredIssues]);

  // --- SP metrics ---
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const spMetrics = useMemo(() => {
    const total = filteredIssues.reduce((s: number, i: Issue) => s + i.sp, 0);
    const done = filteredIssues.filter(isDone).reduce((s: number, i: Issue) => s + i.sp, 0);
    return { total, done, pct: total > 0 ? Math.round((done / total) * 100) : filteredIssues.length > 0 ? Math.round((filteredIssues.filter(isDone).length / filteredIssues.length) * 100) : 0 };
  }, [filteredIssues]);

  // --- Staff productivity ---
  const staffData = useMemo((): StaffMember[] => {
    const map: Record<string, StaffMember> = {};
    filteredIssues.forEach((i: Issue) => {
      const a = i.assignee;
      if (a === "Unassigned" || isExcludedStaff(a)) return;
      if (!map[a]) map[a] = { name: a, issues: [], backlog: 0, totalSP: 0 };
      map[a].issues.push(i);
      map[a].totalSP += i.sp;
    });
    // Merge backlog-only people
    filteredBacklog.forEach((i: Issue) => {
      const a = i.assignee;
      if (a === "Unassigned" || isExcludedStaff(a)) return;
      if (!map[a]) map[a] = { name: a, issues: [], backlog: 0, totalSP: 0 };
      map[a].backlog++;
    });
    // Also count from API backlogByAssignee for people with no backlog issues in response
    if (data?.backlogByAssignee) {
      for (const [name, count] of Object.entries(data.backlogByAssignee)) {
        if (name === "Unassigned" || isExcludedStaff(name)) continue;
        if (map[name] && map[name].backlog === 0) map[name].backlog = count as number;
      }
    }
    return Object.values(map).sort((a, b) => b.issues.length - a.issues.length);
  }, [filteredIssues, filteredBacklog, data?.backlogByAssignee]);

  // --- Stakeholder data (from API reporters) ---
  const stakeholderData = useMemo((): StakeholderMember[] => {
    if (!data?.reporters) return [];
    return data.reporters.map((r: Reporter) => {
      // Apply project filter client-side
      const issues = (r.issues || []).filter((i: Issue) => projectFilter === "ALL" || i.project === projectFilter);
      const bugs = issues.filter((i: Issue) => (i.type || "").toLowerCase() === "bug").length;
      const tasks = issues.filter((i: Issue) => { const t = (i.type || "").toLowerCase(); return t !== "bug" && t !== "epic"; }).length;
      const epics = issues.filter((i: Issue) => (i.type || "").toLowerCase() === "epic").length;
      const done = issues.filter(isDone).length;
      const sc = [0, 0, 0, 0, 0];
      issues.forEach((i: Issue) => { sc[stageIndex(i.statusGroup)]++; });
      return { name: r.name, issues, total: issues.length, bugs, tasks, epics, done, stageCounts: sc };
    }).sort((a: StakeholderMember, b: StakeholderMember) => b.total - a.total);
  }, [data?.reporters, projectFilter]);

  // --- Intelligence insights ---
  const insights = useMemo((): Insights => {
    const health: InsightItem[] = [], performance: InsightItem[] = [], risks: InsightItem[] = [];
    const total = filteredIssues.length;
    if (total === 0) return { health, performance, risks };

    const completionRate = Math.round((filteredIssues.filter(isDone).length / total) * 100);
    const todoRate = Math.round((stageCounts[0] / total) * 100);

    if (completionRate < 30) health.push({ type: "warning", text: `Completion at ${completionRate}% — below target pace` });
    else health.push({ type: "good", text: `Sprint ${completionRate}% complete` });
    if (todoRate > 40) health.push({ type: "critical", text: `${todoRate}% of issues still in To Do — risk of carryover` });
    if (stageCounts[3] > 10) health.push({ type: "info", text: `${stageCounts[3]} issues in UAT — possible bottleneck` });

    const topDev = staffData[0];
    if (topDev) performance.push({ type: "good", text: `${topDev.name.split(" ")[0]} leads with ${topDev.issues.length} issues (${topDev.totalSP} effort)` });
    const overloaded = staffData.filter(d => d.issues.length > 15);
    if (overloaded.length > 0) performance.push({ type: "warning", text: `${overloaded.length} member(s) with 15+ issues — check capacity` });
    const avgIssues = staffData.length > 0 ? Math.round(staffData.reduce((s, d) => s + d.issues.length, 0) / staffData.length) : 0;
    performance.push({ type: "info", text: `Average ${avgIssues} issues per developer` });

    if (bugMetrics.raised > 15) risks.push({ type: "critical", text: `${bugMetrics.raised} bugs — quality concern` });
    else if (bugMetrics.raised > 8) risks.push({ type: "warning", text: `${bugMetrics.raised} bugs — monitor closely` });
    else risks.push({ type: "good", text: `${bugMetrics.raised} bugs — within normal range` });
    if (bugMetrics.raised > 0 && bugMetrics.solved / bugMetrics.raised < 0.5) risks.push({ type: "warning", text: `Only ${Math.round((bugMetrics.solved / bugMetrics.raised) * 100)}% bugs resolved` });
    if (bugMetrics.hotfixes > 0) risks.push({ type: "critical", text: `${bugMetrics.hotfixes} hotfix/critical bug(s)` });

    return { health, performance, risks };
  }, [filteredIssues, stageCounts, staffData, bugMetrics]);

  const ss = data?.sprintStatus;

  // --- Sprint Signal (traffic light: On Track / At Risk / Behind) ---
  const sprintSignal = useMemo((): SprintSignal => {
    const total = filteredIssues.length;
    if (total === 0 || !ss) return { label: "No Data", color: "#71717A", bg: "#71717A1A" };

    const doneCount = filteredIssues.filter(isDone).length;
    const completionPct = Math.round((doneCount / total) * 100);

    const start = new Date(ss.startDate).getTime();
    const end = new Date(ss.endDate).getTime();
    const now = Date.now();
    const elapsed = Math.max(0, Math.min(1, (now - start) / (end - start)));
    const timeElapsedPct = Math.round(elapsed * 100);

    const hasCritical = insights.health.some(h => h.type === "critical") || insights.risks.some(r => r.type === "critical");
    const hasWarning = insights.health.some(h => h.type === "warning") || insights.risks.some(r => r.type === "warning");

    // Behind: completion trails time by 20+% OR any critical flag
    if (completionPct < timeElapsedPct - 20 || hasCritical) {
      return { label: "Behind", color: "#EF4444", bg: "#EF44441A" };
    }
    // At Risk: completion trails time by 10+% OR any warning flag
    if (completionPct < timeElapsedPct - 10 || hasWarning) {
      return { label: "At Risk", color: "#FBBF24", bg: "#FBBF241A" };
    }
    return { label: "On Track", color: "#34D399", bg: "#34D3991A" };
  }, [filteredIssues, ss, insights]);

  // --- Simple Buckets (4 plain-English categories) ---
  const simpleBuckets = useMemo((): SimpleBucket[] => {
    const total = filteredIssues.length || 1;
    return [
      { label: "Not Started", count: stageCounts[0], color: "#8B8BFF", pct: Math.round((stageCounts[0] / total) * 100) },
      { label: "In Progress", count: stageCounts[1], color: "#00b8ff", pct: Math.round((stageCounts[1] / total) * 100) },
      { label: "In Review",   count: stageCounts[2] + stageCounts[3], color: "#FBBF24", pct: Math.round(((stageCounts[2] + stageCounts[3]) / total) * 100) },
      { label: "Done",        count: stageCounts[4], color: "#34D399", pct: Math.round((stageCounts[4] / total) * 100) },
    ];
  }, [filteredIssues.length, stageCounts]);

  // --- Bug Summary (plain-English bug health) ---
  const bugSummary = useMemo((): BugSummary => {
    const { raised, solved, hotfixes } = bugMetrics;
    const resolvePct = raised > 0 ? Math.round((solved / raised) * 100) : 100;
    const resolveColor = resolvePct >= 75 ? "#34D399" : resolvePct >= 50 ? "#FBBF24" : "#EF4444";
    let sentence = "";
    if (raised === 0) {
      sentence = "No bugs reported this sprint.";
    } else {
      sentence = `${solved} of ${raised} bug${raised !== 1 ? "s" : ""} fixed.`;
      if (hotfixes > 0) sentence += ` ${hotfixes} critical bug${hotfixes !== 1 ? "s" : ""} need${hotfixes === 1 ? "s" : ""} attention.`;
      else if (resolvePct === 100) sentence += " All clear!";
    }
    return { raised, solved, hotfixes, resolvePct, resolveColor, sentence };
  }, [bugMetrics]);

  // --- Deployment lists (Dev / UAT / Production) ---
  const deploymentLists = useMemo((): DeploymentLists => {
    const buckets: { dev: DeploymentBucket; uat: DeploymentBucket; prod: DeploymentBucket } = {
      dev: { label: "Deployed to Dev", items: [], grouped: [] },
      uat: { label: "Deployed to UAT", items: [], grouped: [] },
      prod: { label: "Deployed to Production", items: [], grouped: [] },
    };
    filteredIssues.forEach((i: Issue) => {
      const sg = i.statusGroup;
      if (sg === "dev" || sg === "testing") buckets.dev.items.push(i);
      else if (sg === "uat" || sg === "readyProd") buckets.uat.items.push(i);
      else if (sg === "production" || sg === "done") buckets.prod.items.push(i);
    });
    // Group each bucket by type
    const typeOrder = ["Bug", "Story", "Task", "Epic", "Sub-task"];
    for (const bucket of Object.values(buckets)) {
      const byType: Record<string, Issue[]> = {};
      bucket.items.forEach((i: Issue) => {
        const t = i.type || "Other";
        if (!byType[t]) byType[t] = [];
        byType[t].push(i);
      });
      bucket.grouped = typeOrder.filter(t => byType[t]).map(t => ({ type: t, issues: byType[t] }));
      // Add any types not in typeOrder
      Object.keys(byType).filter(t => !typeOrder.includes(t)).forEach(t => {
        bucket.grouped.push({ type: t, issues: byType[t] });
      });
    }
    return buckets;
  }, [filteredIssues]);

  // --- Derived values ---
  const projects = useMemo((): string[] => {
    if (!data?.issues) return ["ALL"];
    return ["ALL", ...Array.from(new Set(data.issues.map((i: Issue) => i.project).filter(Boolean)))];
  }, [data?.issues]);

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;

  const toggleStaff = useCallback((name: string) => setExpandedStaff(p => ({ ...p, [name]: !p[name] })), []);
  const toggleStakeholder = useCallback((name: string) => setExpandedStakeholder(p => ({ ...p, [name]: !p[name] })), []);

  const TABS: TabConfig[] = [
    { key: "overview", label: "Sprint Overview" },
    { key: "team", label: "Team Performance" },
    { key: "quality", label: "Quality & Tracking" },
    { key: "staff-productivity", label: "Staff Productivity" },
  ];

  // --- Loading / Error ---
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-edge-2 border-t-zen-purple rounded-full animate-spin mx-auto mb-4" />
          <p className="text-txt-3 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (err && !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-400 font-semibold mb-2">Failed to load</p>
          <p className="text-txt-3 text-sm mb-4">{err}</p>
          <button onClick={refresh} className="px-4 py-2 rounded-lg bg-zen-purple text-white text-sm font-semibold">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1480px] mx-auto px-6 py-5 min-h-screen">
      <style>{`@keyframes spin { to { transform: rotate(360deg) } } .animate-spin { animation: spin 1s linear infinite; }`}</style>

      {/* === GLOBAL HEADER === */}
      <header className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-zen-purple to-zen-cyan flex items-center justify-center">
            <span className="text-white font-bold text-sm">Z</span>
          </div>
          <div>
            <h1 className="text-lg font-bold text-txt-1 leading-tight">Sprint Productivity Dashboard</h1>
            <p className="text-[11px] text-txt-4 font-medium">Zenvoya Engineering</p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Project tabs */}
          <div className="flex bg-surface-3 rounded-lg p-0.5">
            {projects.map(p => (
              <button key={p} onClick={() => setProjectFilter(p)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  projectFilter === p ? "bg-zen-purple text-white shadow-sm" : "text-txt-3 hover:text-txt-2"
                }`}>
                {p === "ALL" ? "All" : p}
              </button>
            ))}
          </div>

          {/* Sprint countdown */}
          {ss && (
            <div className="bg-surface-3 border border-edge-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-txt-2">
              {ss.daysRemaining} days left
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <input type="text" placeholder="Search..." value={search} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              className="bg-surface-3 border border-edge-1 rounded-lg pl-8 pr-3 py-1.5 text-xs text-txt-2 placeholder-txt-4 w-44 focus:outline-none focus:ring-1 focus:ring-zen-purple" />
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-txt-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx={11} cy={11} r={8} /><path d="M21 21l-4.35-4.35" />
            </svg>
          </div>

          {/* Refresh */}
          <button onClick={refresh} className="bg-surface-3 border border-edge-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-txt-3 hover:text-txt-1 transition-colors">
            Refresh
          </button>
          <span className="text-[11px] font-mono text-txt-4">{mins}:{secs.toString().padStart(2, "0")}</span>
        </div>
      </header>

      {/* === TAB BAR === */}
      <nav className="flex gap-1 border-b border-edge-1 mb-5">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-xs font-semibold transition-all border-b-2 -mb-px ${
              tab === t.key ? "border-zen-purple text-zen-purple" : "border-transparent text-txt-3 hover:text-txt-2"
            }`}>
            {t.label}
          </button>
        ))}
      </nav>

      {/* ================================================================ */}
      {/* TAB 1: SPRINT OVERVIEW                                           */}
      {/* ================================================================ */}
      {tab === "overview" && (
        <div className="space-y-5">

          {/* -- Section 1: Sprint Pulse (hero card) -- */}
          {ss ? (
            <div className="bg-surface-2 border border-edge-1 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                {/* Left: sprint name + date range */}
                <div>
                  <h2 className="text-base font-bold text-txt-1">Current Sprint</h2>
                  <p className="text-xs text-txt-3 mt-0.5">
                    {new Date(ss.startDate).toLocaleDateString()} — {new Date(ss.endDate).toLocaleDateString()} · {ss.daysRemaining} days left
                  </p>
                </div>

                {/* Center: item-count progress */}
                <div className="text-center">
                  <div className="text-2xl font-bold font-mono text-txt-1">
                    {filteredIssues.filter(isDone).length}{" "}
                    <span className="text-sm font-semibold text-txt-3">of</span>{" "}
                    {filteredIssues.length}
                  </div>
                  <div className="text-[11px] text-txt-4 font-medium">items done</div>
                </div>

                {/* Right: traffic-light badge */}
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold"
                  style={{ color: sprintSignal.color, background: sprintSignal.bg }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: sprintSignal.color }} />
                  {sprintSignal.label}
                </span>
              </div>

              {/* Progress bar (item-based) */}
              <div className="h-2.5 rounded-full bg-surface-4 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${filteredIssues.length > 0 ? Math.round((filteredIssues.filter(isDone).length / filteredIssues.length) * 100) : 0}%`,
                    background: sprintSignal.color,
                  }} />
              </div>
            </div>
          ) : (
            <div className="bg-surface-2 border border-edge-1 rounded-xl p-5">
              <p className="text-txt-3 text-sm">No active sprint found.</p>
            </div>
          )}

          {/* -- Section 2: Where Things Stand (4 buckets) -- */}
          <div>
            <SectionHeader title="Where Things Stand" subtitle={`${filteredIssues.length} items across all stages`} />
            <div className="grid grid-cols-4 gap-3 mb-3">
              {simpleBuckets.map(b => (
                <div key={b.label} className="bg-surface-2 border border-edge-1 rounded-xl p-4 flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-txt-3 uppercase tracking-wider">{b.label}</span>
                  <div className="flex items-end gap-2">
                    <span className="text-2xl font-bold font-mono" style={{ color: b.color }}>{b.count}</span>
                    <span className="text-[11px] text-txt-4 mb-0.5">{b.pct}%</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Stacked horizontal bar */}
            <div className="h-3 rounded-full overflow-hidden flex bg-surface-4">
              {simpleBuckets.map(b => (
                b.count > 0 && (
                  <div key={b.label} className="h-full transition-all duration-500"
                    style={{ width: `${b.pct}%`, background: b.color, minWidth: b.count > 0 ? "4px" : 0 }}
                    title={`${b.label}: ${b.count}`} />
                )
              ))}
            </div>
          </div>

          {/* -- Section 3: Bug Health -- */}
          <div>
            <SectionHeader title="Bug Health" subtitle="Are bugs getting fixed or piling up?" />
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-surface-2 border border-edge-1 rounded-xl p-4 flex flex-col gap-1">
                <span className="text-[11px] font-medium text-txt-3 uppercase tracking-wider">Found</span>
                <span className="text-2xl font-bold font-mono text-txt-1">{bugSummary.raised}</span>
              </div>
              <div className="bg-surface-2 border border-edge-1 rounded-xl p-4 flex flex-col gap-1">
                <span className="text-[11px] font-medium text-txt-3 uppercase tracking-wider">Fixed</span>
                <span className="text-2xl font-bold font-mono text-emerald-400">{bugSummary.solved}</span>
              </div>
              <div className="bg-surface-2 border border-edge-1 rounded-xl p-4 flex flex-col gap-1">
                <span className="text-[11px] font-medium text-txt-3 uppercase tracking-wider">Critical Bugs</span>
                <span className={`text-2xl font-bold font-mono ${bugSummary.hotfixes > 0 ? "text-red-400" : "text-txt-1"}`}>{bugSummary.hotfixes}</span>
              </div>
            </div>
            {/* Resolution progress bar */}
            <div className="bg-surface-2 border border-edge-1 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-medium text-txt-3 uppercase tracking-wider">Resolution Progress</span>
                <span className="text-sm font-bold font-mono" style={{ color: bugSummary.resolveColor }}>{bugSummary.resolvePct}%</span>
              </div>
              <div className="h-2 rounded-full bg-surface-4 overflow-hidden mb-2">
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${bugSummary.resolvePct}%`, background: bugSummary.resolveColor }} />
              </div>
              <p className="text-[12px] text-txt-2">{bugSummary.sentence}</p>
            </div>
          </div>

          {/* -- Section 4: What's Been Deployed -- */}
          <div>
            <SectionHeader title="What's Been Deployed" subtitle="Items shipped to each environment this sprint" />
            <div className="space-y-4">
              {([
                { key: "dev", bucket: deploymentLists.dev },
                { key: "uat", bucket: deploymentLists.uat },
                { key: "prod", bucket: deploymentLists.prod },
              ] as { key: string; bucket: DeploymentBucket }[]).map(({ key, bucket }) => {
                const isOpen = !!expandedDeployments[key];
                return (
                  <div key={key} className="bg-surface-2 border border-edge-1 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedDeployments(p => ({ ...p, [key]: !p[key] }))}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-surface-3/50 transition-colors">
                      <h4 className="text-sm font-bold text-txt-1">
                        {bucket.label} — <span className="font-mono">{bucket.items.length}</span> items
                      </h4>
                      <span className="text-txt-4 text-xs">{isOpen ? "▼ collapse" : "▸ expand"}</span>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4">
                        {bucket.grouped.length > 0 ? bucket.grouped.map(group => (
                          <div key={group.type} className="mb-3 last:mb-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <TypeIcon type={group.type} />
                              <span className="text-[11px] font-semibold text-txt-2 uppercase tracking-wider">{group.type}s ({group.issues.length})</span>
                            </div>
                            <div className="space-y-0.5 ml-7">
                              {group.issues.map(t => (
                                <div key={t.key} className="flex items-center gap-2.5 py-1 border-b border-edge-1 last:border-0">
                                  <span className="font-mono font-semibold text-zen-purple text-[11px] w-20 shrink-0">{t.key}</span>
                                  <span className="flex-1 text-txt-2 text-[12px] truncate">{t.summary}</span>
                                  <span className="text-txt-3 text-[11px] shrink-0">{t.assignee}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )) : (
                          <p className="text-txt-4 text-xs">No items in this environment yet.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      )}

      {/* ================================================================ */}
      {/* TAB 2: TEAM PERFORMANCE                                          */}
      {/* ================================================================ */}
      {tab === "team" && (
        <div className="space-y-5">
          <div className="bg-surface-2 border border-edge-1 rounded-xl p-5">
            <SectionHeader title="Staff Productivity" subtitle="Current Jira status breakdown per team member — click to expand" />
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[860px]">
                <thead>
                  <tr className="border-b border-edge-2">
                    <th className="text-left py-2.5 px-3 font-semibold text-txt-3 uppercase text-[10px] tracking-wider sticky left-0 bg-surface-2 z-10 min-w-[180px]">Member</th>
                    <th className="text-center py-2.5 px-2 font-bold text-txt-2 text-[10px] uppercase">Total</th>
                    <th className="text-center py-2.5 px-2 font-semibold text-amber-400 text-[10px] uppercase">Backlog</th>
                    {STAGES.map((s, i) => (
                      <th key={s} className="text-center py-2.5 px-2 font-semibold text-[10px] uppercase" style={{ color: STAGE_COLORS[i] }}>{s.split("/")[0]}</th>
                    ))}
                    <th className="text-center py-2.5 px-2 font-semibold text-zen-purple text-[10px] uppercase">SP</th>
                    <th className="text-center py-2.5 px-2 font-semibold text-emerald-400 text-[10px] uppercase">Done %</th>
                  </tr>
                </thead>
                <tbody>
                  {staffData.map(dev => {
                    const devStages = [0, 0, 0, 0, 0];
                    dev.issues.forEach((i: Issue) => { devStages[stageIndex(i.statusGroup)]++; });
                    const donePct = dev.issues.length > 0 ? Math.round((devStages[4] / dev.issues.length) * 100) : 0;

                    return (
                      <Fragment key={dev.name}>
                        <tr className="border-b border-edge-1 cursor-pointer hover:bg-surface-3/50 transition-colors"
                          onClick={() => toggleStaff(dev.name)}>
                          <td className="py-2.5 px-3 sticky left-0 bg-surface-2 z-10">
                            <div className="flex items-center gap-2.5">
                              <Avatar name={dev.name} />
                              <div>
                                <span className="font-semibold text-txt-1 text-[12px]">{dev.name}</span>
                                {dev.issues.length === 0 && <span className="text-[9px] text-amber-400 ml-1">backlog only</span>}
                                <div className="text-[9px] text-txt-4">{expandedStaff[dev.name] ? "▼ collapse" : "▸ expand"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="text-center py-2.5 font-bold text-sm text-txt-1">{dev.issues.length}</td>
                          <td className="text-center py-2.5">
                            <span className={`font-semibold ${dev.backlog > 0 ? "text-amber-400" : "text-txt-4"}`}>{dev.backlog}</span>
                          </td>
                          {devStages.map((count, si) => (
                            <td key={si} className="text-center py-2.5">
                              {count > 0 ? (
                                <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded-md font-bold text-[12px]"
                                  style={{ background: STAGE_COLORS[si] + "1A", color: STAGE_COLORS[si] }}>{count}</span>
                              ) : (
                                <span className="text-txt-4">-</span>
                              )}
                            </td>
                          ))}
                          <td className="text-center py-2.5 font-bold text-zen-purple font-mono">{dev.totalSP}</td>
                          <td className="text-center py-2.5">
                            <span className={`font-bold ${donePct >= 50 ? "text-emerald-400" : donePct > 0 ? "text-amber-400" : "text-txt-4"}`}>{donePct}%</span>
                          </td>
                        </tr>
                        {expandedStaff[dev.name] && (
                          <tr>
                            <td colSpan={10} className="bg-surface-1 px-4 py-3 border-b border-edge-1">
                              {dev.issues.length > 0 ? (
                                <div className="space-y-1">
                                  {dev.issues.map(t => (
                                    <div key={t.key} className="flex items-center gap-2.5 py-1.5 border-b border-edge-1 last:border-0">
                                      <TypeIcon type={t.type} />
                                      <span className="font-mono font-semibold text-zen-purple text-[11px] w-20">{t.key}</span>
                                      <span className="flex-1 text-txt-2 text-[12px] truncate">{t.summary}</span>
                                      <span className="text-txt-4 text-[10px] font-mono">{t.sp} SP</span>
                                      <Badge status={t.status} />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-txt-4 text-xs">No sprint issues — backlog only</span>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {/* Totals row */}
                  <tr className="border-t-2 border-edge-2 bg-surface-3/30">
                    <td className="py-3 px-3 font-bold text-txt-1 text-[12px] sticky left-0 bg-surface-2 z-10">Total ({staffData.length} members)</td>
                    <td className="text-center py-3 font-bold text-sm text-txt-1">{staffData.reduce((s, d) => s + d.issues.length, 0)}</td>
                    <td className="text-center py-3 font-bold text-amber-400">{staffData.reduce((s, d) => s + d.backlog, 0)}</td>
                    {[0, 1, 2, 3, 4].map(si => {
                      const total = staffData.reduce((s, d) => {
                        let c = 0; d.issues.forEach((i: Issue) => { if (stageIndex(i.statusGroup) === si) c++; }); return s + c;
                      }, 0);
                      return <td key={si} className="text-center py-3 font-bold" style={{ color: STAGE_COLORS[si] }}>{total}</td>;
                    })}
                    <td className="text-center py-3 font-bold text-zen-purple font-mono">{staffData.reduce((s, d) => s + d.totalSP, 0)}</td>
                    <td className="text-center py-3 font-bold text-emerald-400">
                      {(() => {
                        const t = staffData.reduce((s, d) => s + d.issues.length, 0);
                        const d = staffData.reduce((s, dev) => s + dev.issues.filter(isDone).length, 0);
                        return t > 0 ? Math.round((d / t) * 100) : 0;
                      })()}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* TAB 3: QUALITY & TRACKING                                        */}
      {/* ================================================================ */}
      {tab === "quality" && (
        <div className="space-y-5">
          {/* Bug metrics */}
          <div className="grid grid-cols-2 gap-3">
            <KPICard label="Bugs Found" value={bugMetrics.raised} sub="this sprint" />
            <KPICard label="Bugs Fixed" value={bugMetrics.solved} sub="fix deployed or verified" />
          </div>

          {/* Open Bugs */}
          <div className="bg-surface-2 border border-edge-1 rounded-xl p-5">
            <SectionHeader title="Open Bugs" subtitle="Bugs not yet resolved" />
            {(() => {
              const openBugs = filteredIssues.filter((i: Issue) => (i.type || "").toLowerCase() === "bug" && !isBugSolved(i));
              return openBugs.length > 0 ? (
                <div className="space-y-1">
                  {openBugs.map(t => (
                    <div key={t.key} className="flex items-center gap-2.5 py-1.5 border-b border-edge-1 last:border-0">
                      <span className="font-mono font-semibold text-red-400 text-[11px] w-20">{t.key}</span>
                      <span className="flex-1 text-txt-2 text-[12px] truncate">{t.summary}</span>
                      <span className="text-txt-3 text-[11px]">{t.assignee}</span>
                      <span className={`text-[10px] font-semibold ${t.priority === "Highest" ? "text-red-400" : t.priority === "High" ? "text-amber-400" : "text-txt-4"}`}>{t.priority}</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
                        style={{ color: envLabelColor(t.statusGroup), background: envLabelColor(t.statusGroup) + "1A" }}>
                        {plainEnvLabel(t.statusGroup)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-emerald-400 text-sm">All bugs resolved!</p>
              );
            })()}
          </div>

          {/* Production Deployments */}
          <div className="bg-surface-2 border border-edge-1 rounded-xl p-5">
            <SectionHeader title="Production Deployments" subtitle="Items live in production this sprint" />
            {(() => {
              const prodIssues = filteredIssues.filter((i: Issue) => i.status === "DEPLOYMENT IN PRODUCTON");
              return prodIssues.length > 0 ? (
                <div className="space-y-1">
                  {prodIssues.map(t => (
                    <div key={t.key} className="flex items-center gap-2.5 py-1.5 border-b border-edge-1 last:border-0">
                      <TypeIcon type={t.type} />
                      <span className="font-mono font-semibold text-zen-purple text-[11px] w-20">{t.key}</span>
                      <span className="flex-1 text-txt-2 text-[12px] truncate">{t.summary}</span>
                      <span className="text-txt-3 text-[11px]">{t.assignee}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-txt-4 text-sm">No production deployments yet.</p>
              );
            })()}
          </div>

          {/* Raised by Stakeholders */}
          <div className="bg-surface-2 border border-edge-1 rounded-xl p-5">
            <SectionHeader title="Raised by Stakeholders" subtitle="Issues created by key stakeholders across SMB, SMA, ZAP — click to expand" />
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[580px]">
                <thead>
                  <tr className="border-b border-edge-2">
                    <th className="text-left py-2.5 px-3 font-semibold text-txt-3 uppercase text-[10px] tracking-wider">Stakeholder</th>
                    <th className="text-center py-2.5 px-2 font-bold text-txt-2 text-[10px] uppercase">Total</th>
                    <th className="text-center py-2.5 px-2 font-semibold text-red-400 text-[10px] uppercase">Bugs</th>
                    <th className="text-center py-2.5 px-2 font-semibold text-blue-400 text-[10px] uppercase">Tasks</th>
                    <th className="text-center py-2.5 px-2 font-semibold text-emerald-400 text-[10px] uppercase">Done %</th>
                  </tr>
                </thead>
                <tbody>
                  {stakeholderData.map(sh => {
                    const donePct = sh.total > 0 ? Math.round((sh.done / sh.total) * 100) : 0;
                    return (
                      <Fragment key={sh.name}>
                        <tr className="border-b border-edge-1 cursor-pointer hover:bg-surface-3/50 transition-colors border-l-2 border-l-zen-purple"
                          onClick={() => toggleStakeholder(sh.name)}>
                          <td className="py-2.5 px-3">
                            <div className="flex items-center gap-2.5">
                              <Avatar name={sh.name} />
                              <div>
                                <span className="font-semibold text-txt-1 text-[12px]">{sh.name}</span>
                                <div className="text-[9px] text-txt-4">{expandedStakeholder[sh.name] ? "▼ collapse" : "▸ see issues"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="text-center py-2.5 font-bold text-sm text-txt-1">{sh.total}</td>
                          <td className="text-center py-2.5"><span className={`font-semibold ${sh.bugs > 0 ? "text-red-400" : "text-txt-4"}`}>{sh.bugs}</span></td>
                          <td className="text-center py-2.5"><span className={`font-semibold ${sh.tasks > 0 ? "text-blue-400" : "text-txt-4"}`}>{sh.tasks}</span></td>
                          <td className="text-center py-2.5">
                            <span className={`font-bold ${donePct >= 50 ? "text-emerald-400" : donePct > 0 ? "text-amber-400" : "text-txt-4"}`}>{donePct}%</span>
                          </td>
                        </tr>
                        {expandedStakeholder[sh.name] && (
                          <tr>
                            <td colSpan={5} className="bg-surface-1 px-4 py-3 border-b border-edge-1">
                              {sh.issues.length > 0 ? (
                                <div className="space-y-1">
                                  {sh.issues.map(t => (
                                    <div key={t.key} className="flex items-center gap-2.5 py-1.5 border-b border-edge-1 last:border-0">
                                      <TypeIcon type={t.type} />
                                      <span className="font-mono font-semibold text-zen-purple text-[11px] w-20">{t.key}</span>
                                      <span className="flex-1 text-txt-2 text-[12px] truncate">{t.summary}</span>
                                      <span className="text-txt-3 text-[11px]">{t.assignee}</span>
                                      <Badge status={t.status} />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-txt-4 text-xs">No issues from this stakeholder in current sprint</span>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* TAB 4: STAFF PRODUCTIVITY                                        */}
      {/* ================================================================ */}
      {tab === "staff-productivity" && (
        <StaffProductivity globalProject={projectFilter} />
      )}

      {/* === FOOTER === */}
      <footer className="mt-10 pt-4 border-t border-edge-1 flex items-center justify-between text-[11px] text-txt-4">
        <span>Sprint Productivity Dashboard · Zenvoya Engineering</span>
        <span className="font-mono">
          Last updated: {data?.timestamp ? new Date(data.timestamp).toLocaleString() : "—"}
        </span>
      </footer>
    </div>
  );
}
